import ErrorBoundary from './components/ErrorBoundary';
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAuthStore from './store/authStore';
import './styles/theme.css';

// Website Pages
import Home from './pages/website/Home';
import Plans from './pages/website/Plans';
import About from './pages/website/About';
import Contact from './pages/website/Contact';
import Privacy from './pages/website/Privacy';
import Terms from './pages/website/Terms';

// Auth Pages
import AdminLogin from './pages/admin/AdminLogin';
import ClientLogin from './pages/client/ClientLogin';
import ClientRegister from './pages/client/ClientRegister';

// Layouts
import ClientLayout from './components/layout/ClientLayout';
import AdminLayout from './components/layout/AdminLayout';

// Client Pages
import ClientDashboard from './pages/client/ClientDashboard';
import ClientSettings from './pages/client/ClientSettings';
import Campaigns from './pages/client/Campaigns';
import Leads from './pages/client/Leads';
import CRM from './pages/client/CRM';
import EmailAccounts from './pages/client/EmailAccounts';
import Warmup from './pages/client/Warmup';
import Inbox from './pages/client/Inbox';
import Verifier from './pages/client/Verifier';
import ABTesting from './pages/client/ABTesting';
import Scheduling from './pages/client/Scheduling';
import Templates from './pages/client/Templates';
import Analytics from './pages/client/Analytics';
import CampaignReport from './pages/client/CampaignReport';
import Blacklist from './pages/client/Blacklist';
import Impersonate from './pages/admin/Impersonate';
import ClientStats from './pages/admin/ClientStats';
import Billing from './pages/client/Billing';
import Sequences from './pages/client/Sequences';
import Payment from './pages/client/Payment';
import Invoice from './pages/client/Invoice';
import Support from './pages/client/Support';
import Unsubscribes from './pages/client/Unsubscribes';
import OAuthCallback from './pages/OAuthCallback';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import Clients from './pages/admin/Clients';
import DeletedClients from './pages/admin/DeletedClients';
import PlanPricing from './pages/admin/PlanPricing';
import Revenue from './pages/admin/Revenue';
import ActivityLogs from './pages/admin/ActivityLogs';
import AdminSettings from './pages/admin/AdminSettings';
import AdminTickets from './pages/admin/AdminTickets';
import BankTransfers from './pages/admin/BankTransfers';
import AdminInvoices from './pages/admin/AdminInvoices';

// Placeholder for remaining pages
const Placeholder = ({ title }) => (
  <div style={{ textAlign: 'center', padding: 60 }}>
    <div style={{ fontSize: 48, marginBottom: 16 }}>🚧</div>
    <h2 style={{ color: '#2ECC71', marginBottom: 8 }}>{title}</h2>
    <p style={{ color: '#555' }}>This page is ready to receive its component</p>
  </div>
);

// ── GUARDS ────────────────────────────────────────
function ClientGuard({ children }) {
  const { user, token, fetchMe, setUser } = useAuthStore();
  const [checked, setChecked] = React.useState(false);
  useEffect(() => {
    if (token && !user) {
      fetchMe().then(u => {
        if (u) setUser(u);
        setChecked(true);
      }).catch(() => setChecked(true));
    } else {
      setChecked(true);
    }
  }, []);
  if (!checked) return <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2ECC71', fontSize: 16 }}>⏳ Loading...</div>;
  if (!token) return <Navigate to="/client" replace />;
  if (user && user.role === 'superadmin') return <Navigate to="/admin/dashboard" replace />;
  if (!user) return <Navigate to="/client" replace />;
  return <ClientLayout>{children}</ClientLayout>;
}

function AdminGuard({ children }) {
  const { user, token, fetchMe, setUser } = useAuthStore();
  const [checked, setChecked] = React.useState(false);
  useEffect(() => {
    if (token && !user) {
      fetchMe().then(u => {
        if (u) setUser(u);
        setChecked(true);
      }).catch(() => setChecked(true));
    } else {
      setChecked(true);
    }
  }, []);
  if (!checked) return <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2ECC71', fontSize: 16 }}>⏳ Loading...</div>;
  if (!token) return <Navigate to="/admin" replace />;
  if (user && user.role !== 'superadmin') return <Navigate to="/client/dashboard" replace />;
  if (!user) return <Navigate to="/admin" replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{ style: { background: '#1a1a1a', color: '#fff', border: '1px solid rgba(46,204,113,0.2)' }, success: { iconTheme: { primary: '#2ECC71', secondary: '#000' } } }} />
      <Routes>
        {/* Website */}
        <Route path="/" element={<Home />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />

        {/* OAuth Callbacks — must be public, no auth required */}
        <Route path="/oauth/google/callback" element={<OAuthCallback />} />
        <Route path="/oauth/microsoft/callback" element={<OAuthCallback type="microsoft" />} />

        {/* Admin Auth */}
        <Route path="/admin" element={<AdminLogin />} />
        {/* Admin Protected */}
        <Route path="/admin/dashboard" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
        <Route path="/admin/clients" element={<AdminGuard><Clients /></AdminGuard>} />
        <Route path="/admin/deleted" element={<AdminGuard><DeletedClients /></AdminGuard>} />
        <Route path="/admin/plans" element={<AdminGuard><PlanPricing /></AdminGuard>} />
        <Route path="/admin/revenue" element={<AdminGuard><Revenue /></AdminGuard>} />
        <Route path="/admin/activity" element={<AdminGuard><ActivityLogs /></AdminGuard>} />
        <Route path="/admin/settings" element={<AdminGuard><AdminSettings /></AdminGuard>} />
        <Route path="/admin/bank-transfers" element={<AdminGuard><BankTransfers /></AdminGuard>} />
        <Route path="/admin/invoices" element={<AdminGuard><AdminInvoices /></AdminGuard>} />
        <Route path="/admin/tickets" element={<AdminGuard><AdminTickets /></AdminGuard>} />
        <Route path="/admin/impersonate/:id" element={<AdminGuard><Impersonate /></AdminGuard>} />
        <Route path="/admin/clients/:id/stats" element={<AdminGuard><ClientStats /></AdminGuard>} />

        {/* Client Auth */}
        <Route path="/client" element={<ClientLogin />} />
        <Route path="/client/register" element={<ClientRegister />} />
        <Route path="/client/payment" element={<Payment />} />

        {/* Client Protected */}
        <Route path="/client/dashboard" element={<ClientGuard><ClientDashboard /></ClientGuard>} />
        <Route path="/client/campaigns" element={<ClientGuard><Campaigns /></ClientGuard>} />
        <Route path="/client/campaigns/:id/report" element={<ClientGuard><CampaignReport /></ClientGuard>} />
        <Route path="/client/sequences" element={<ClientGuard><Sequences /></ClientGuard>} />
        <Route path="/client/leads" element={<ClientGuard><Leads /></ClientGuard>} />
        <Route path="/client/crm" element={<ClientGuard><CRM /></ClientGuard>} />
        <Route path="/client/email-accounts" element={<ClientGuard><EmailAccounts /></ClientGuard>} />
        <Route path="/client/warmup" element={<ClientGuard><Warmup /></ClientGuard>} />
        <Route path="/client/inbox" element={<ClientGuard><Inbox /></ClientGuard>} />
        <Route path="/client/verifier" element={<ClientGuard><Verifier /></ClientGuard>} />
        <Route path="/client/ab-testing" element={<ClientGuard><ABTesting /></ClientGuard>} />
        <Route path="/client/scheduling" element={<ClientGuard><Scheduling /></ClientGuard>} />
        <Route path="/client/templates" element={<ClientGuard><Templates /></ClientGuard>} />
        <Route path="/client/analytics" element={<ClientGuard><Analytics /></ClientGuard>} />
        <Route path="/client/billing" element={<ClientGuard><Billing /></ClientGuard>} />
        <Route path="/client/invoices" element={<ClientGuard><Invoice /></ClientGuard>} />
        <Route path="/client/support" element={<ClientGuard><Support /></ClientGuard>} />
        <Route path="/client/unsubscribes" element={<ClientGuard><Unsubscribes /></ClientGuard>} />
        <Route path="/client/blacklist" element={<ClientGuard><Blacklist /></ClientGuard>} />
        <Route path="/client/settings" element={<ClientGuard><ClientSettings /></ClientGuard>} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
